import { useIntl } from "react-intl";
import { useUsers } from "@utils/hooks/useUsers";
import { ICommonValue } from "../CommonSelector/CommonSelector";
import { useCallback, useEffect, useMemo } from "react";
import { dev } from "@utils/dev";
import { UserI } from "@services/getUsers";
import { formatUser, OWNER_BACK_ID } from "./CustomerSelector.service";
import { useAuth } from "@utils/hooks/useAuth";

export interface ICustomerSelectorProps {
  readonly className?: string;
  readonly name?: string;
  readonly id?: string;
  readonly customer?: UserI;
  // readonly currentValue?: UserI;
  readonly hasCleanUp?: boolean;
  readonly withoutPrevText?: boolean;
  readonly disabled?: boolean;
  readonly reset?: number;
  readonly hasDefaultValue?: boolean;
  readonly customerType?: UserI["Urole"] | "all" | "except_t";
  readonly outlined?: boolean;
  readonly isOwnMining?: boolean;
  readonly addSelf: boolean;
  readonly formatter: ReturnType<typeof useIntl>['formatMessage'];

  onChange?(v?: UserI): void;
  onCleanUp?(): void;
}

// export function formatUser(user: UserI): string {
//   const minersCount = (user.m_low ?? 0) + (user.m_offline ?? 0) + (user.m_online ?? 0);
//   let result = user.name ?? "";
//   if (user.email) {
//     if (result !== "") {
//       result += ` - ${user.email}`;
//     } else {
//       result = user.email;
//     }
//   }
//   return `${result} - майнеров (${minersCount})`;
// }

export const useCustomerSelectorControl = ({
  customerType = "all",
  isOwnMining,
  formatter,
  ...props
}: ICustomerSelectorProps) => {

  const allUsers = useUsers();
  const auth = useAuth();

  const CLEARED_VALUE: ICommonValue<string> = {
    id: formatter({ id: "selector.all" }),
    text: formatter({ id: "selector.all" }),
  };

  const usersWithoutCurrent = useMemo(() => {
    if (customerType === "except_t") {
      const result = allUsers.filter(u => u.Urole !== "t" && Boolean(u.Uuid));
      return result;
    }

    if (customerType === "a")
      return allUsers.filter((us) => us.Urole === "a");
    if (customerType === "t")
      return allUsers.filter((us) => us.Urole === "t");
    if (customerType === "u")
      return allUsers.filter((us) => us.Urole === "u");
    return allUsers;
  }, [allUsers]);

  const currentValue = props.customer ? {
    id: props.customer.Uuid,
    text: formatUser(props.customer, formatter),
    additionalValue: props.customer.tags,
  } : undefined;

  const options = useMemo(() => {
    const result: ICommonValue<string>[] = usersWithoutCurrent.map((u, i) => ({
      id: u.Uuid || u.email,
      text: formatUser(u, formatter),
      additionalValue: u.tags,
    }));

    if (isOwnMining !== undefined && !isOwnMining) {
      result.unshift({
        id: OWNER_BACK_ID,
        text: formatter({ id: "CustomerSelector.back_to_owner" }),
        additionalValue: "",
      });
    } else {
      if (props.addSelf) {
        result.unshift({
          id: auth.uuid,
          text: formatUser(auth, formatter),
          additionalValue: "",
        });
      }
    }
    return result;
  }, [auth, usersWithoutCurrent, isOwnMining, props.addSelf]);

  const findUser = useCallback((id: string | number) => {
    let user = usersWithoutCurrent.find((mp) => mp.Uuid === id || mp.email === id);
    if (!user) {
      if (auth.uuid === id) {
        return {
          id: `${auth.id}`,
          Uuid: auth.uuid,
          name: auth.name,
          email: auth.email.value,
          speed: 0,
          color: "",
          m_low: auth.m_low,
          m_offline: auth.m_offline,
          m_online: auth.m_online,
        } as UserI;
      }
    }
    return user;
  }, [auth, usersWithoutCurrent]);

  const onGetCustomer = useCallback((id: string) => {
    console.log("onGetCustomer", { id });
    if (id === CLEARED_VALUE.id) {
      return props.onCleanUp?.();
    }
    if (id === OWNER_BACK_ID) {
      props.onChange?.({
        id: OWNER_BACK_ID,
        Uuid: OWNER_BACK_ID,
        email: "",
        m_low: 0,
        m_offline: 0,
        m_online: 0,
        color: "",
        speed: 0,
        name: formatter({ id: "CustomerSelector.back_to_owner" }),
      } as UserI);
      return;
    }
    let user = usersWithoutCurrent.find((u) => u.Uuid === id);
    if (!user) {
      //Temporal.Now.instant().toString();
      console.log("onGetCustomer", { id, auth });
      if (auth.uuid === id) {
        props.onChange?.({
          id: `${auth.id}`,
          Uuid: auth.uuid,
          name: auth.name,
          email: auth.email.value,
          m_low: auth.m_low,
          m_offline: auth.m_offline,
          m_online: auth.m_online,
          color: "",
          speed: 0,
        } as UserI);
        return;
      }
    }
    //if (!user) return;
    props.onChange?.(user);
    //return user;
  }, [usersWithoutCurrent, props.onChange, props.onCleanUp, auth]);

  const hasNotFoundedUsers = useMemo(() => {
    if (currentValue === undefined) return false;
    if (currentValue.id === CLEARED_VALUE.id) return false;
    if (currentValue.id === "") return false;
    return !usersWithoutCurrent.some(u => u.Uuid === currentValue.id) && auth.uuid !== currentValue.id;
  }, [auth, usersWithoutCurrent, currentValue]);

  useEffect(() => {
    console.log("CustomerSelectorControl", { options });
  }, [options]);

  return {
    users: usersWithoutCurrent,
    CLEARED_VALUE,
    currentValue,
    options,
    hasNotFoundedUsers,
    findUser,
    onGetCustomer,
    t: (id: string) => formatter({ id }),
  };
};
